import { Component, Inject, Input, OnInit } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';

interface RowData {
  offloadItem: string;
  poSelections: { [poNumber: string]: string | null };
  poOptions: { [poNumber: string]: string[] };
  overloadPo: string | null;
  overloadOptions: string[];
}

@Component({
  selector: 'app-offload-mapping',
  templateUrl: './offload-mapping.component.html',
})
export class OffloadMappingComponent implements OnInit {
  @Input() notMappedItemsJson: string;
  @Input() notMappedOffItemsJson: string;

  poNumbers: string[] = [];
  poItemsMap: { [poNumber: string]: string[] } = {};
  rows: RowData[] = [];

  errorMessage: string | null = null;
  submitting = false;

  constructor(private drawerRef: NzDrawerRef<string>) {}

  ngOnInit(): void {
    this.buildData();
  }

  private buildData(): void {
    this.poItemsMap = this.parseObject(this.notMappedItemsJson);
    const offloadItems = this.parseArray(this.notMappedOffItemsJson);

    this.poNumbers = Object.keys(this.poItemsMap);

    this.rows = offloadItems.map((offloadItem) => {
      const poSelections: { [poNumber: string]: string | null } = {};
      const poOptions: { [poNumber: string]: string[] } = {};
      let overloadOptions = [...this.poNumbers];

      this.poNumbers.forEach((po) => {
        const sourceItems = this.poItemsMap[po] || [];
        const hasDirectMatch = sourceItems.includes(offloadItem);

        poSelections[po] = null;
        poOptions[po] = hasDirectMatch ? [offloadItem] : [...sourceItems];

        if (hasDirectMatch) {
          overloadOptions = overloadOptions.filter((x) => x !== po);
        }
      });

      return {
        offloadItem,
        poSelections,
        poOptions,
        overloadPo: null,
        overloadOptions,
      };
    });
  }

  getPoLabel(index: number, poNumber: string): string {
    return `PO${index + 1}: ${poNumber}`;
  }

  onPoSelectionChange(row: RowData, currentPo: string): void {
    const currentValue = row.poSelections[currentPo];

    if (currentValue) {
      this.poNumbers.forEach((po) => {
        if (po !== currentPo) {
          row.poSelections[po] = null;
        }
      });
      row.overloadPo = null;
    }
  }

  onOverloadChange(row: RowData): void {
    if (row.overloadPo) {
      this.poNumbers.forEach((po) => {
        row.poSelections[po] = null;
      });
    }
  }

  isPoDisabled(row: RowData, currentPo: string): boolean {
    if (row.overloadPo) {
      return true;
    }

    return this.poNumbers.some(
      (po) => po !== currentPo && !!row.poSelections[po],
    );
  }

  isOverloadDisabled(row: RowData): boolean {
    return this.poNumbers.some((po) => !!row.poSelections[po]);
  }

  isRowMapped(row: RowData): boolean {
    const hasPoMapping = this.poNumbers.some((po) => !!row.poSelections[po]);
    const hasOverloadMapping = !!row.overloadPo;

    return hasPoMapping || hasOverloadMapping;
  }

  submit(): void {
    this.errorMessage = null;

    const unmappedRows = this.rows.filter((row) => !this.isRowMapped(row));

    if (unmappedRows.length > 0) {
      this.errorMessage =
        'Please map all offloading items before submitting. Unmapped items: ' +
        unmappedRows.map((x) => x.offloadItem).join(', ');
      return;
    }

    this.submitting = true;

    const result = this.getResult();
    console.log('submit result', result);

    this.drawerRef.close(JSON.stringify(result));

    this.submitting = false;
  }

  private parseObject(json: string): { [key: string]: string[] } {
    try {
      return json ? JSON.parse(json) : {};
    } catch {
      return {};
    }
  }

  private parseArray(json: string): string[] {
    try {
      return json ? JSON.parse(json) : [];
    } catch {
      return [];
    }
  }

  getResult(): any[] {
    return this.rows.map((row) => {
      const selectedPo = Object.keys(row.poSelections).find(
        (po) => !!row.poSelections[po],
      );

      if (selectedPo) {
        return {
          offloadItem: row.offloadItem,
          mappingType: 'PO',
          poNumber: selectedPo,
          mappedItem: row.poSelections[selectedPo],
        };
      }

      if (row.overloadPo) {
        return {
          offloadItem: row.offloadItem,
          mappingType: 'OVERLOAD',
          poNumber: row.overloadPo,
          mappedItem: null,
        };
      }

      return {
        offloadItem: row.offloadItem,
        mappingType: null,
        poNumber: null,
        mappedItem: null,
      };
    });
  }
}
